import { useEffect, useRef, useState } from "react";
import styles from "./customizeModal.module.scss";
import Button from "@components/base/button";
import axios from "axios";
import { getApiUrl } from "@utils/baseUrl";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { analyticsDashletConfig, selectedAnalyticsDashlet } from "@redux/Analytics/analyticsDashletConfigSlice";
import ReactTooltip from "react-tooltip";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";

const CustomizeAnalytics = ({ sortAnalyticsWidgets, showCustomiseWidget, setShowCustomiseWidget, analysis, trade_direction, country, handleLoader, currentUserPlan, pathname, analyticsCountryID, gridCacheKey }) => {
  const dispatch = useDispatch();
  const selectedDashlets = useSelector((state) => state.analyticsDashletConfig?.analyticsDashletConfig);
  const [dashlets, setDashlets] = useState(JSON.parse(JSON.stringify(selectedDashlets)));
  const [drawerPosition, setDrawerPosition] = useState({
    position: "absolute",
    top: "0px",
    right: "0px",
  });
  const widgetRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // Determine positioning based on scroll position
      if (scrollTop > 215) {
        setDrawerPosition({
          position: "fixed",
          top: "50px",
          right: "20px",
        });
      } else {
        setDrawerPosition({
          position: "absolute",
          top: "0px",
          right: "0px",
        });
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Initial positioning
    handleScroll();

    // Cleanup event listener
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setDashlets(JSON.parse(JSON.stringify(selectedDashlets)));
  }, [showCustomiseWidget]);

  const onDragStart = (e, index) => {
    e.dataTransfer.setData("itemIndex", index);
  };

  const onDragOver = (index) => {
    trademoTrackingEvents(trackingEventDictionary.analyticsTabCustomizeWidgetsReorderWidget, generateTrackingObject(analyticsCountryID, pathname, currentUserPlan));
    const draggedOverItemIndex = parseInt(event.dataTransfer.getData("itemIndex"));

    if (draggedOverItemIndex === index) {
      return;
    }

    const newItems = [...dashlets];
    const draggedItem = newItems[draggedOverItemIndex];
    newItems.splice(draggedOverItemIndex, 1);
    newItems.splice(index, 0, draggedItem);

    setDashlets([...newItems]);
    event.dataTransfer.setData("itemIndex", index);
  };

  const onDragEnd = () => {
    event.dataTransfer.clearData();
  };

  const handleSelectDashlet = (isChecked, key) => {
    const newItems = [...dashlets];
    for (let i = 0; i < newItems.length; i++) {
      if (newItems[i]?.key === key) {
        newItems[i].is_selected = isChecked;
        break;
      }
    }
    setDashlets([...newItems]);
  };

  const saveConfigure = async (isReset) => {
    let selectedArr = [];
    if (!isReset) {
      trademoTrackingEvents(trackingEventDictionary.analyticsTabCustomizeWidgetsSaveConfiguration, generateTrackingObject(analyticsCountryID, pathname, currentUserPlan));
      for (let i = 0; i < dashlets.length; i++) {
        if (dashlets[i]?.is_selected === true) {
          selectedArr.push(dashlets[i]?.key);
        }
      }
    } else {
      trademoTrackingEvents(trackingEventDictionary.analyticsTabCustomizeWidgetsResetToDefault, generateTrackingObject(analyticsCountryID, pathname, currentUserPlan));
    }
    let payload = {
      is_reset: isReset ? true : false,
      user_selection_dashlets: [...selectedArr],
      trade_direction: trade_direction || "",
      country: country || "",
      analysis: analysis,
    };
    handleLoader(true);
    setShowCustomiseWidget(true);
    try {
      let response = await axios.post(getApiUrl("setUserCustomizedDashlets"), payload);
      let savedOrder = Array.isArray(response?.data?.user_selection_dashlets) && response.data.user_selection_dashlets.length ? [...response.data.user_selection_dashlets] : [...selectedArr];
      sortAnalyticsWidgets([...savedOrder]);
      let updatedDashlets = storeDataToRedux([...savedOrder]);
      updateGridCacheDashlets([...savedOrder], updatedDashlets);
      toast.success(response?.data?.message || "User configuration set successfully");
    } catch {
      toast.error(`Something went wrong. Please try again later!`);
    } finally {
      handleLoader(false);
    }
  };

  const storeDataToRedux = (arr) => {
    let obj = {};
    let tempDash = JSON.parse(JSON.stringify(dashlets));
    for (let i = 0; i < tempDash.length; i++) {
      if (arr.includes(tempDash[i].key)) {
        tempDash[i].is_selected = true;
      } else {
        tempDash[i].is_selected = false;
      }
      obj[tempDash[i].key] = tempDash[i];
    }
    let reorderArr = [];
    for (let i = 0; i < arr.length; i++) {
      if (obj[arr[i]]) {
        reorderArr.push(obj[arr[i]]);
        delete obj[arr[i]];
      }
    }
    reorderArr = [...reorderArr, ...Object.values(obj)];
    dispatch(analyticsDashletConfig(JSON.parse(JSON.stringify(reorderArr))));
    dispatch(selectedAnalyticsDashlet([...arr]));
    return reorderArr;
  };

  const updateGridCacheDashlets = (selectionOrder, configurableDashlets) => {
    if (!gridCacheKey) return;
    try {
      let gridCacheStates = JSON.parse(sessionStorage.getItem("gridCache"));
      if (!gridCacheStates || !gridCacheStates[gridCacheKey]) return;
      let cachedEntry = gridCacheStates[gridCacheKey];
      const newCacheData = {
        ...gridCacheStates,
        [gridCacheKey]: {
          ...cachedEntry,
          data: {
            ...cachedEntry?.data,
            analytics_dashlet: {
              ...cachedEntry?.data?.analytics_dashlet,
              user_selection_dashlets: [...selectionOrder],
              configurable_dashlets: JSON.parse(JSON.stringify(configurableDashlets || [])),
            },
          },
        },
      };
      sessionStorage.setItem("gridCache", JSON.stringify(newCacheData));
    } catch {
      // cache refresh is best effort - the server stays the source of truth on the next fetch
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target) && showCustomiseWidget) {
        setShowCustomiseWidget(true);
      }
    };

    // Add event listener when drawer is open
    if (showCustomiseWidget) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCustomiseWidget]);

  return (
    <>
      {
        <div
          ref={widgetRef}
          className="tw-right-0 tw-w-[302px] tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
          style={{
            position: drawerPosition.position,
            top: `${drawerPosition.top}`,
            right: `${drawerPosition.right}`,
            height: "calc(100vh - 0px)",
          }}>
          <div className="tw-h-full tw-overflow-y-auto">
            <div className="tw-absolute tw-flex tw-items-center tw-justify-center tw-h-5 tw-w-5 tw-left-[-8px] tw-bg-white tw-top-[-8px] tw-cursor-pointer tw-border tw-rounded-3xl tw-border-gray-300" onClick={() => setShowCustomiseWidget(true)}>
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                <path d="M291-253.85 253.85-291l189-189-189-189L291-706.15l189 189 189-189L706.15-669l-189 189 189 189L669-253.85l-189-189-189 189Z" />
              </svg>
            </div>
            <div className="tw-flex tw-flex-col tw-bg-white tw-z-[1]tw-gap-4 tw-px-5 tw-py-4">
              <div className="tw-flex tw-flex-col tw-gap-4 tw-mb-[106px]">
                {dashlets.map((item, index) => {
                  return (
                    <div draggable={item.is_selected ? true : false} onDragStart={(e) => onDragStart(e, index)} onDragEnd={onDragEnd} onDragOver={(e) => e.preventDefault()} onDrop={() => onDragOver(index)} className={`tw-flex tw-cursor-pointer ${!item.is_selected ? "tw-bg-gray-100" : "tw-bg-white"} tw-gap-2 tw-w-[262px] tw-h-full tw-p-1 tw-border tw-rounded-lg`} key={`analytics_custom_row_${item.label_name}`}>
                      {item.is_selected ? (
                        <span className={`${styles.grab_handle} tw-flex tw-items-center`}>
                          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                            <path d="M359.79-192Q330-192 309-213.21t-21-51Q288-294 309.21-315t51-21Q390-336 411-314.79t21 51Q432-234 410.79-213t-51 21Zm240 0Q570-192 549-213.21t-21-51Q528-294 549.21-315t51-21Q630-336 651-314.79t21 51Q672-234 650.79-213t-51 21Zm-240-216Q330-408 309-429.21t-21-51Q288-510 309.21-531t51-21Q390-552 411-530.79t21 51Q432-450 410.79-429t-51 21Zm240 0Q570-408 549-429.21t-21-51Q528-510 549.21-531t51-21Q630-552 651-530.79t21 51Q672-450 650.79-429t-51 21Zm-240-216Q330-624 309-645.21t-21-51Q288-726 309.21-747t51-21Q390-768 411-746.79t21 51Q432-666 410.79-645t-51 21Zm240 0Q570-624 549-645.21t-21-51Q528-726 549.21-747t51-21Q630-768 651-746.79t21 51Q672-666 650.79-645t-51 21Z" />
                          </svg>
                        </span>
                      ) : (
                        <span className={`${styles.grab_handle} tw-flex tw-items-center`}>
                          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#98A2B3">
                            <path d="M359.79-192Q330-192 309-213.21t-21-51Q288-294 309.21-315t51-21Q390-336 411-314.79t21 51Q432-234 410.79-213t-51 21Zm240 0Q570-192 549-213.21t-21-51Q528-294 549.21-315t51-21Q630-336 651-314.79t21 51Q672-234 650.79-213t-51 21Zm-240-216Q330-408 309-429.21t-21-51Q288-510 309.21-531t51-21Q390-552 411-530.79t21 51Q432-450 410.79-429t-51 21Zm240 0Q570-408 549-429.21t-21-51Q528-510 549.21-531t51-21Q630-552 651-530.79t21 51Q672-450 650.79-429t-51 21Zm-240-216Q330-624 309-645.21t-21-51Q288-726 309.21-747t51-21Q390-768 411-746.79t21 51Q432-666 410.79-645t-51 21Zm240 0Q570-624 549-645.21t-21-51Q528-726 549.21-747t51-21Q630-768 651-746.79t21 51Q672-666 650.79-645t-51 21Z" />
                          </svg>
                        </span>
                      )}
                      <div className="tw-gap-5 tw-flex tw-items-center">
                        <label className="form-check !tw-pl-0">
                          <input className={`form-check-input ${styles.input_css}`} type="checkbox" checked={item.is_selected} onChange={(e) => handleSelectDashlet(e.target.checked, item.key)} />
                        </label>
                        <span className="tw-text-sm tw-font-semibold">{item.label_name}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={`tw-flex tw-px-5 tw-w-[302px] tw-py-3 tw-border-t tw-border-gray-200 justify-content-start align-items-center tw-z-[1] tw-bg-white tw-fixed tw-bottom-0 tw-gap-2`}>
              <span data-for="analytics-configure-save-button" data-tip={dashlets.filter((item) => item.is_selected === true).length < 5 ? "Select minimum 5 widgets" : ""}>
                <Button size="small" iconSize="small" disabled={dashlets.filter((item) => item.is_selected === true).length < 5} onClick={() => saveConfigure()} variant="primaryWithIcon">
                  Apply
                </Button>
              </span>
              <ReactTooltip className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="bottom" id="analytics-configure-save-button" />
              <Button size="small" iconSize="small" onClick={() => saveConfigure("reset")} variant="secondaryWithIcon">
                Reset to default
              </Button>
            </div>
          </div>
        </div>
      }
    </>
  );
};
export default CustomizeAnalytics;
